import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions, Platform, Linking } from 'react-native';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ADMIN_COLORS, DESKTOP_CONFIRM_COPY, FUNDING_PATH } from '@/constants/admin';
import { AdminNavList } from '@/components/admin/nav/AdminNavList';
import LinearGradient from 'react-native-linear-gradient';
import { useAdminScreen } from '@/hooks/admin/useAdminScreen';
import { ResioLogo } from '@/assets/ResioLogo';
import { PropertyOrgSelectors } from '@/components/admin/nav/PropertyOrgSelectors';
import { useAdminNavigation } from '@/hooks/admin/useAdminNavigation';
import { useDisplaySelectors } from '@/hooks/admin/useDisplaySelectors';
import { useAppDispatch } from '@/store';
import { setSelectedOrganizationId, setSelectedPropertyId } from '@/store/slices/admin/adminUISlice';
import { WebConfirmModal } from '@/components/common/modals';
import { getScreenMetadata } from '@/utils/admin/navigation';

interface AdminSidebarProps {
  routeName: keyof AdminStackParamList;
  onNavigate: (path: string) => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ routeName, onNavigate }) => {
  const { handleSignOut } = useAdminScreen(routeName);
  const currentPath = useMemo(() => getScreenMetadata(routeName).path, [routeName]);
  const { adminUser } = useAdminNavigation();
  const displaySelectors = useDisplaySelectors(currentPath);
  const dispatch = useAppDispatch();
  const isFunding = useMemo(() => currentPath.includes(FUNDING_PATH), [currentPath]);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const handleOrganizationChange = useCallback((orgId: number) => {
    dispatch(setSelectedOrganizationId(orgId));
    const availableProperty = adminUser.properties?.find(prop => prop.organizationId === orgId);
    if (availableProperty) {
      dispatch(setSelectedPropertyId(availableProperty.id));
    }
  }, [dispatch, adminUser.properties]);

  const handlePropertyChange = useCallback((propId: number) => {
    dispatch(setSelectedPropertyId(propId));
  }, [dispatch]);

  const handleSignOutClick = useCallback(() => {
    setShowSignOutModal(true);
  }, []);

  const handleConfirmSignOut = useCallback(() => {
    setShowSignOutModal(false);
    handleSignOut();
  }, [handleSignOut]);

  const handleCancelSignOut = useCallback(() => {
    setShowSignOutModal(false);
  }, []);

  const { height: windowHeight } = useWindowDimensions();
  const containerHeight = Platform.OS === 'web' ? { height: windowHeight } : undefined;

  return (
    <View style={[styles.container, containerHeight]}>
      <LinearGradient
        // Match mobile GradientBorderFrame (Figma 180.09deg): blue -> purple -> red
        colors={[
          "#4773FE",
          "#5b39ac",
          "#ab3470",
        ]}
        locations={[0.1138, 0.6385, 0.9773]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={styles.gradientBackground}
      />
      <View style={styles.inner}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => onNavigate('/residents')} activeOpacity={0.8} style={styles.logoContainer}>
            <ResioLogo width={40} height={40} />
            <Text style={styles.logoText}>RESIO</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.orgPropSection}>
          <PropertyOrgSelectors
            organizations={adminUser.organizations || []}
            properties={adminUser.properties || []}
            selectedOrganizationId={adminUser.selectedOrganizationId}
            selectedPropertyId={adminUser.selectedPropertyId}
            onOrganizationChange={handleOrganizationChange}
            onPropertyChange={handlePropertyChange}
            showPropertySelector={!isFunding && displaySelectors.includes('property')}
            disableOrganization={!displaySelectors.includes('organization')}
            disabled={false}
          />
        </View>
        <ScrollView
          style={styles.navContainer}
          contentContainerStyle={styles.navScrollContent}
          showsVerticalScrollIndicator={false}
        >
          <AdminNavList currentPath={currentPath} onNavigate={onNavigate} />
        </ScrollView>
        <View style={styles.footerContainer}>
          <TouchableOpacity
            onPress={() => Linking.openURL('https://www.resio.com/')}
            activeOpacity={0.8}
            accessibilityRole={'link' as any}
            accessibilityLabel="Learn more about Resio"
          >
            <Text style={styles.learnMoreText}>
              Learn more about us at{"\n"}
              <Text style={styles.learnMoreHighlight}>RESIO.COM</Text>
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleSignOutClick}
            activeOpacity={0.8}
            style={styles.logoutButton}
            accessibilityRole={'button' as any}
            accessibilityLabel="Sign Out"
          >
            <Text style={styles.logoutButtonText}>Sign Out</Text>
          </TouchableOpacity>
        </View>
      </View>
      <WebConfirmModal
        visible={showSignOutModal}
        title={DESKTOP_CONFIRM_COPY.signOut.title}
        message={DESKTOP_CONFIRM_COPY.signOut.message}
        confirmText={DESKTOP_CONFIRM_COPY.signOut.confirmText}
        cancelText={DESKTOP_CONFIRM_COPY.signOut.cancelText}
        onConfirm={handleConfirmSignOut}
        onCancel={handleCancelSignOut}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
    position: 'relative',
    // height assigned dynamically for web via useWindowDimensions
    borderTopRightRadius: 30,
    overflow: 'hidden',
  },
  inner: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 4, // leave 4px reveal for the gradient border
    backgroundColor: ADMIN_COLORS.NAVIGATION.BACKGROUND,
    borderTopRightRadius: 30,
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  logoContainer: { flexDirection: 'row', alignItems: 'flex-start' },
  orgPropSection: {
    width: '100%',
    backgroundColor: ADMIN_COLORS.NAVIGATION.BACKGROUND,
    paddingTop: 12,
    paddingRight: 20,
    paddingBottom: 10,
    paddingLeft: 20,
  },
  navContainer: {
    flex: 1,
  },
  navScrollContent: {
    flexGrow: 1,
    padding: 16,
  },
  footerContainer: {
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 20,
    gap: 12,
  },
  gradientBackground: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
  },
  logoText: {
    fontSize: 30,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    letterSpacing: 0.5,
  },
  learnMoreText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
  },
  learnMoreHighlight: {
    color: ADMIN_COLORS.TEXT.ACCENT,
  },
  logoutButton: {
    alignSelf: 'stretch',
    backgroundColor: ADMIN_COLORS.SURFACE,
    borderColor: ADMIN_COLORS.BUTTON.PRIMARY_BACKGROUND,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutButtonText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 14,
    fontWeight: '600',
  },
});
